import styled from "styled-components";
import React, { useEffect, useRef, useState } from "react";

import {
  setToastState,
  TInvoice,
  TNexaasProduct,
} from "../../../../redux/reducers/globalState";

import Button, { TButtonIconVariants, TButtonProps } from "../../Button";
import TableGrid, {
  TArrayCell,
  TCell,
  TCellStyleVariants,
  TTableBody,
} from "../../TableGrid";
import TabsGroup from "../../../ui/groups/TabsGroup";
import { format } from "date-fns/format";
import SummaryCard from "../../SummaryCard";
import apiService from "../../../../services/api";
import {
  backendErrorMessageHandler,
  getQuerystringParams,
} from "../../../../utils/miscellaneous";
import axios, { CancelTokenSource } from "axios";
import { useDispatch } from "react-redux";
import Tooltip from "../../Tooltip";
import { parseISO } from "date-fns/parseISO";

const Container = styled.form`
  & > section:nth-child(3) {
    margin-top: 3px;
  }
`;

const CardsContainer = styled.div<{ $row: "single" | "double" }>`
  display: flex;
  gap: 16px;

  & > div {
    width: ${({ $row }) => ($row === "single" ? "100%" : "50%")};
  }
`;

const Footer = styled.footer`
  margin-top: 24px;
  border-top: 1px solid var(--color-light-200);
  padding: 0px;
  padding-top: 16px;
  display: flex;
  justify-content: flex-end;

  .textContainer {
    b,
    p {
      font-size: 14px;
      color: var(--color-text-paragraph);
    }

    b {
      font-family: var(--font-paragraph-bold);
    }

    p {
      font-family: var(--font-paragraph-default);
    }
  }

  .buttonsContainer {
    display: flex;
    justify-content: flex-end;

    & button:not(& button:last-child) {
      margin-right: 12px;
    }
  }
`;

export type TPayableObjectForm = {
  amount: string;
  category_id: number | null;
  classification_center_id: number | null;
  description: string;
  due_date: string;
  mode: null | "in_cash" | "recurrent" | "installments";
  supplier_id: number | null;

  paid_amount?: string;
  fine_amount?: string;
  interest_amount?: string;
  discount_amount?: string;
  payment_date?: string;
  period?:
    | "weekly"
    | "biweekly"
    | "monthly"
    | "bimonthly"
    | "quarterly"
    | "semiannual"
    | "annual";
  installments?: number;
  income_tax_relevant?: boolean;
};

type TGoodsProps = {
  data: {
    invoiceId: string;
    success: TNexaasProduct[];
    error: TNexaasProduct[];
  };
  footer: { buttons: TButtonProps<TButtonIconVariants>[] };
};

const GoodsStockCheck: React.FC<TGoodsProps> = ({ data, footer }) => {
  const errorDataRef = useRef(data.error ?? []);
  const successDataRef = useRef(data.success ?? []);

  const hasOrderNumber = [...(data.error ?? []), ...(data.success ?? [])].some(
    (p) => p.order_number != null && p.order_number !== ""
  );

  const [isButtonLoading, setIsButtonLoading] = useState(
    Array.from({ length: 3 }, () => false)
  );

  const [isLoading, setIsLoading] = useState(true);

  const [shoulUpdateComponent, setShoulUpdateComponent] = useState(false);

  const [errorData, setErrorData] = useState<TNexaasProduct[]>([]);
  const [successData, setSuccessData] = useState<TNexaasProduct[]>([]);

  const [invoice, setInvoice] = useState<TInvoice | null>(null);

  const cancelTokenSource = useRef<CancelTokenSource | null>(null);

  const dispatch = useDispatch();

  const handleCheckNoSku = (data: TNexaasProduct[]) =>
    Array.isArray(data)
      ? data?.filter((value) =>
          /* value.sku === null */ value.reason?.includes("sku_not_found")
        )
      : [];

  const handleCheckNoBatchInfo = (data: TNexaasProduct[]) =>
    Array.isArray(data)
      ? data?.filter((value) =>
          /* !value.batch_expiration_date || !value.batch_manufacturing_date */ value.reason?.includes(
            "batch"
          )
        )
      : [];

  const handleCheckHasConversionFactor = (data: TNexaasProduct[]) =>
    Array.isArray(data)
      ? data?.filter((value) => value.conversion_factor !== null)
      : [];

  const onSubmitHandler = (
    onClick: ((props?: any) => void) | undefined,
    index: number
  ) => {
    setIsButtonLoading((prev) => {
      const temp = [...prev];
      temp[index] = !temp[index];

      return temp;
    });

    if (index === 0) {
      const queryStrings = getQuerystringParams();

      if (queryStrings.organization_id && cancelTokenSource.current) {
        setIsLoading(true);

        apiService
          .getNonCachedGoodsExpressModeStatus(
            queryStrings.organization_id,
            data.invoiceId,
            cancelTokenSource.current.token
          )
          .then((response: any) => {
            setErrorData(response.data.errors ?? []);
            setSuccessData(response.data.success ?? []);

            errorDataRef.current = response.data.errors ?? [];
            successDataRef.current = response.data.success ?? [];

            setShoulUpdateComponent(true);
            setTimeout(() => {
              setShoulUpdateComponent(false);
            }, 50);
          })
          .catch((e) => {
            const errorMessage = backendErrorMessageHandler(e);

            dispatch(
              setToastState({
                variant: "error",
                shouldShow: true,
                message: {
                  title: "Erro ao atualizar",
                  description:
                    typeof errorMessage === "string"
                      ? errorMessage
                      : "Não foi possível atualizar os dados, tente novamente",
                },
              })
            );
          })
          .finally(() => {
            setTimeout(() => {
              setIsLoading(false);
            }, 500);
            setIsButtonLoading((prev) => {
              const temp = [...prev];
              temp[index] = !temp[index];

              return temp;
            });
            if (onClick) onClick();
          });
      }
    } else {
      if (onClick) onClick();
    }
  };

  const rowFormatter = (product: TNexaasProduct, index: number) => {
    const skuCell: TArrayCell<TCell<Exclude<TCellStyleVariants, "button">>> = [
      {
        style: "default",
        value: product.supplier_sku,
        marginRight: "8px",
        shouldBreakLine: true,
      },
      {
        style: "brokenLink",
        icon: "brokenLink",
        iconPosition: "left",
        value: "Não encontrado",
      },
    ];
    skuCell.forceFlex = true;

    const skuCellWithFlex = Object.assign([], skuCell, { forceFlex: true });

    return [
      {
        style: "default",
        value:
          product.index !== undefined && product.index !== null
            ? product.index.toString()
            : "",
      },
      ,
      // SKU/Cód. Fornecedor column
      product.sku === null
        ? skuCellWithFlex
        : product.sku === product.supplier_sku
        ? [
            {
              style: "default",
              value: product.sku,
              marginRight: "8px",
              shouldBreakLine: true,
            },
          ]
        : [
            {
              style: "default",
              value: product.sku,
              marginRight: "8px",
              shouldBreakLine: true,
            },
            {
              style: "default",
              value: product.supplier_sku,
              marginRight: "8px",
            },
          ],
      {
        style: "miscellaneous",
        values: [
          {
            style: "default",
            text: product.description,
            shouldBreakLine: true,
          },
          {
            style: "bold",
            text: "EAN:",
            shouldBreakLine: false,
            marginRight: "8px",
          },
          {
            style: "default",
            text: product.ean,
            shouldBreakLine: false,
          },
        ],
      },
      {
        style: "miscellaneous",
        values: [
          {
            style: "bold",
            text: product.quantity,
            shouldBreakLine: false,
            marginRight: "8px",
          },
          { style: "default", text: product.measure, shouldBreakLine: false },
        ],
      },
      [
        {
          style: "button",
          icon: "arrowRight",
          isDisabled: true,
          customCSS: { paddingLeft: 0 },
        },
      ],
      [
        {
          style: "miscellaneous",
          values: [
            {
              style: "bold",
              text:
                product.verified_quantity !== null
                  ? product.verified_quantity
                  : " - ",
              shouldBreakLine: false,
              marginRight: "8px",
            },
            {
              style: "default",
              text:
                product.verified_quantity !== null
                  ? product.verified_measure ?? "UN"
                  : "",
              marginRight: "8px",
              shouldBreakLine: false,
            },
          ],
        },
        product.conversion_factor !== null && {
          style: "button",
          icon: "exchange",
          tooltip: {
            customTextAlignment: "left",
            customTopPosition: 100 * index + 175,
            text: [
              {
                style: "default",
                text: "Fator de conversão:",
                shouldBreakLine: true,
              },
              {
                style: "default",
                marginRight: "3px",
                text: product.conversion_factor.purchase_quantity,
                shouldBreakLine: false,
              },
              {
                style: "default",
                marginRight: "3px",
                text: product.conversion_factor.purchase_measure,
                shouldBreakLine: false,
              },
              {
                style: "default",
                marginRight: "3px",
                text: "->",
                shouldBreakLine: false,
              },
              {
                style: "default",
                marginRight: "3px",
                text: product.conversion_factor.sales_quantity,
                shouldBreakLine: false,
              },
              {
                style: "default",
                marginRight: "3px",
                text: product.conversion_factor.sales_measure,
                shouldBreakLine: true,
              },
              {
                style: "default",
                text: "Para mudar o fator de conversão,",
                shouldBreakLine: true,
              },
              {
                style: "default",
                text: "acesse o cadastro do produto no OMS",
                shouldBreakLine: false,
              },
            ],
          },
          isDisabled: true,
        },
      ],
      ...(hasOrderNumber
        ? [
            {
              style: "miscellaneous",
              values: product.order_number
                ? [{ style: "default", text: product.order_number, shouldBreakLine: false }]
                : [{ style: "faded", text: "-", shouldBreakLine: false }],
            },
          ]
        : []),
      {
        style: "miscellaneous",
        values:
          !product.batch_name &&
          !product.batch_expiration_date &&
          !product.batch_manufacturing_date
            ? [{ style: "faded", text: "Sem lote", shouldBreakLine: false }]
            : [
                {
                  style: "bold",
                  text: product.batch_name ?? " - ",
                  shouldBreakLine: true,
                },
                {
                  style: "bold",
                  fontSize: "12px",
                  text: "Fab:",
                  marginRight: "3px",
                  shouldBreakLine: false,
                },
                {
                  style: "default",
                  fontSize: "12px",
                  text: product.batch_manufacturing_date
                    ? format(
                        parseISO(product.batch_manufacturing_date),
                        "dd/MM/yyyy"
                      )
                    : " - ",
                  marginRight: "3px",
                  shouldBreakLine: true,
                },
                {
                  style: "bold",
                  fontSize: "12px",
                  text: "Val:",
                  marginRight: "3px",
                  shouldBreakLine: false,
                },
                {
                  style: "default",
                  fontSize: "12px",
                  text: product.batch_expiration_date
                    ? format(
                        parseISO(product.batch_expiration_date),
                        "dd/MM/yyyy"
                      )
                    : " - ",
                  marginRight: "3px",
                  shouldBreakLine: true,
                },
              ],
      },
    ] as TTableBody<TCellStyleVariants>[];
  };

  useEffect(() => {
    setTimeout(() => {
      cancelTokenSource.current = axios.CancelToken.source();
      if (data.invoiceId) {
        setIsLoading(true);
        apiService
          .getInvoice(data.invoiceId, cancelTokenSource.current.token)
          .then((response: any) => {
            if (response && response.data) {
              setInvoice(response.data);
            } else {
              setInvoice(null); // or handle error state
            }
          })
          .catch(() => setInvoice(null))
          .finally(() => {
            setTimeout(() => {
              setIsLoading(false);
            }, 500);
          });
      }
    }, 500);

    if (errorData.length === 0 && successData.length === 0) {
      setErrorData(data.error ?? []);
      setSuccessData(data.success ?? []);
      errorDataRef.current = data.error ?? [];
      successDataRef.current = data.success ?? [];
    }

    return () => {
      if (cancelTokenSource.current)
        cancelTokenSource.current.cancel("from goods stock check, canceling");
    };
  }, []);

  // Calculate tab data and counts
  const tabData = [
    {
      label: "Todas",
      onClick: () => {
        setErrorData(errorDataRef.current ?? []);
        setSuccessData(successDataRef.current ?? []);
      },
      count:
        (errorDataRef.current ?? []).length +
        (successDataRef.current ?? []).length,
      content: (
        <>
          <h2>Todas</h2>
          <div></div>
        </>
      ),
      index: 0,
    },
    {
      label: "SKU não encontrado",
      onClick: () => {
        setErrorData(handleCheckNoSku(errorDataRef.current) ?? []);
        setSuccessData(handleCheckNoSku(successDataRef.current) ?? []);
      },
      count:
        handleCheckNoSku(errorDataRef.current).length +
        handleCheckNoSku(successDataRef.current).length,
      content: (
        <>
          <h2>SKU não encontrado</h2>
        </>
      ),
      index: 1,
    },
    {
      label: "Lote sem data preenchida",
      onClick: () => {
        setErrorData(handleCheckNoBatchInfo(errorDataRef.current) ?? []);
        setSuccessData(handleCheckNoBatchInfo(successDataRef.current) ?? []);
      },
      count:
        handleCheckNoBatchInfo(errorDataRef.current).length +
        handleCheckNoBatchInfo(successDataRef.current).length,
      content: (
        <>
          <h2>Lote sem data preenchida</h2>
          <div></div>
        </>
      ),
      index: 2,
    },
    {
      label: "Com fator de conversão",
      onClick: () => {
        setErrorData(
          handleCheckHasConversionFactor(errorDataRef.current) ?? []
        );
        setSuccessData(
          handleCheckHasConversionFactor(successDataRef.current) ?? []
        );
      },
      count:
        handleCheckHasConversionFactor(errorDataRef.current).length +
        handleCheckHasConversionFactor(successDataRef.current).length,
      content: (
        <>
          <h2>Com fator de conversão</h2>
          <div></div>
        </>
      ),
      index: 3,
    },
  ];

  // Filter out tabs with count === 0 (except "Todas", which should always show)
  const filteredTabs = tabData
    .filter((tab) => tab.count > 0)
    .map((tab, idx) => ({
      ...tab,
      index: idx,
    }));

  return (
    <Container>
      <CardsContainer
        $row={
          [...(errorData ?? []), ...(successData ?? [])].some(
            (value) => value.conversion_factor
          ) &&
          [
            ...(errorData ?? []).filter(
              (error) => !error.reason?.includes("conversion")
            ),
          ].length > 0
            ? "double"
            : "single"
        }
        style={{ display: isLoading ? "none" : undefined }}
      >
        {[
          ...(errorData ?? []).filter(
            (error) => !error.reason?.includes("conversion")
          ),
        ].length > 0 && (
          <SummaryCard
            variant="error"
            messages={[
              handleCheckNoSku(errorData ?? []).length > 0
                ? "- Há itens não encontrados/cadastrados no OMS"
                : "",
              [...handleCheckNoBatchInfo(errorData ?? [])].length > 0
                ? "- Há itens com controle de lote sem as informações de lote preenchidas"
                : "",
            ]}
          />
        )}
        {[...(errorData ?? []), ...(successData ?? [])].some(
          (value) => value.conversion_factor
        ) && (
          <SummaryCard
            variant="warning"
            messages={[
              "Alguns produtos possuem fator de conversão, verifique se estão de acordo com o real recebido",
            ]}
          />
        )}
      </CardsContainer>

      <TabsGroup
        key={shoulUpdateComponent + ""}
        initialActiveTabIndex={0}
        onClickItems={filteredTabs.map((tab) => tab.onClick)}
        stringIcon={filteredTabs
          .filter((_, idx) => idx !== 0) // skip "Todas" for icons if you want
          .map((tab) => {
            return {
              index: tab.index,
              value: tab.count.toString(),
            };
          })}
      >
        {filteredTabs.map((tab) => tab.content)}
      </TabsGroup>
      <TableGrid
        row="singleRow"
        borderColor={
          !isLoading
            ? [
                ...(errorData ?? [])
                  .sort((a, b) => a.index - b.index)
                  .map((error) =>
                    error.reason?.includes("sku_not_found") ||
                    error.reason?.includes("sku_batch_control")
                      ? "red"
                      : "light"
                  ),
                ...(successData ?? [])
                  .sort((a, b) => a.index - b.index)
                  .map(() => "light"),
              ]
            : undefined
        }
        columns={{
          tableHead: {
            width: [hasOrderNumber
              ? `0px 12px 50px 250px 300px 100px 50px 110px 80px 90px`
              : `0px 12px 50px 250px 360px 100px 50px 130px 90px`],
          },
          tableBody: {
            width: [hasOrderNumber
              ? `0px 12px 50px 250px 300px 100px 50px 110px 80px 90px`
              : `0px 12px 50px 250px 360px 100px 50px 130px 90px`],
          },
        }}
        onAction={() => {
          window.location.reload();
        }}
        emptyState={{
          isEmptyState:
            !isLoading && [...errorData, ...successData].length === 0,
          title: "Nenhuma nota encontrada",
          description: "Altere os filtros e realize a busca novamente",
        }}
        selectType={{
          type: "noSelect" /*, fullData: allInvoicesRef.current  */,
        }}
        tableHead={{
          customTopPosition: "-24px",
          customPaddingTop: "8px",
          value: [
            { style: "noIcon", showSkeleton: isLoading, value: "Nº" },
            {
              style: "noIcon",
              showSkeleton: isLoading,
              value: "SKU/Cód. Fornecedor",
            },
            { style: "noIcon", showSkeleton: isLoading, value: "Descrição" },
            { style: "noIcon", showSkeleton: isLoading, value: "Qtde." },
            {
              style: "noIcon",
              showSkeleton: isLoading,
              value: "",
            },
            {
              style: "noIcon",
              showSkeleton: isLoading,
              value: "Qtde. Conferida",
            },
            ...(hasOrderNumber
              ? [{ style: "noIcon" as const, showSkeleton: isLoading, value: "Nº Pedido" }]
              : []),
            { style: "noIcon", showSkeleton: isLoading, value: "Lote" },
          ],
        }}
        isLoading={isLoading}
        tableBody={
          isLoading
            ? new Array(5).fill("").map(() => ({
                elementId: "",
                file_nfe: null,
                file_xml: null,
                body: [
                  {
                    style: "default",
                    value: "",
                    skeleton: { values: [{ width: "30px" }] },
                  },
                  {
                    style: "default",
                    value: "",
                    skeleton: { values: [{ width: "122px" }] },
                  },
                  {
                    style: "default",
                    value: "",
                    skeleton: { values: [{ width: "247px" }] },
                  },
                  {
                    style: "default",
                    value: "",
                    skeleton: { values: [{ width: "80px" }] },
                  },
                  {
                    style: "default",
                    value: "",
                    skeleton: { values: [{ width: "30px" }] },
                  },
                  {
                    style: "default",
                    value: "",
                    skeleton: {
                      values: [
                        {
                          width: "100px",
                          style: {
                            overflow: "hidden",
                          },
                        },
                      ],
                    },
                  },
                  ...(hasOrderNumber
                    ? [
                        {
                          style: "default",
                          value: "",
                          skeleton: {
                            values: [{ width: "80px", style: { overflow: "hidden" } }],
                          },
                        },
                      ]
                    : []),
                  {
                    style: "default",
                    value: "",
                    skeleton: {
                      values: [
                        {
                          width: "90px",
                          style: {
                            overflow: "hidden",
                          },
                        },
                      ],
                    },
                  },
                ] as TTableBody<TCellStyleVariants>[],
              }))
            : [
                ...[...errorData]
                  .sort((a, b) => a.index - b.index)
                  .map((result, index: number) => ({
                    elementId: "",
                    file_nfe: "",
                    file_xml: "",
                    body: rowFormatter(result, index),
                  })),
                ...[...successData]
                  .sort((a, b) => a.index - b.index)
                  .map((result, index: number) => ({
                    elementId: "",
                    file_nfe: "",
                    file_xml: "",
                    body: rowFormatter(result, index),
                  })),
              ]
        }
      />
      <Footer>
        <div className="buttonsContainer">
          {footer.buttons?.map((buttonProps, index) => {
            const { onClick, ...props } = buttonProps;

            const isDisabled =
              index === 2 &&
              ([
                ...(errorDataRef.current ?? []).filter(
                  (error) => !error.reason?.includes("conversion")
                ),
              ].length > 0 ||
                [
                  ...(errorDataRef.current ?? []),
                  ...(successDataRef.current ?? []),
                ].length === 0 ||
                (invoice !== null && invoice.nexaas_state > 40));

            return isDisabled ? (
              <Tooltip
                text={[
                  {
                    style: "default",
                    text: "Corrija os erros no OMS para",
                    shouldBreakLine: true,
                  },
                  {
                    style: "default",
                    text: "finalizar a Entrada",
                    shouldBreakLine: false,
                  },
                ]}
                size="large"
              >
                <Button
                  key={index + " - " + isButtonLoading[index]}
                  role={index === 2 ? "submit" : "button"}
                  {...props}
                  onClick={(e) => {
                    e.preventDefault();
                    onSubmitHandler(onClick, index);
                  }}
                  disabled={isDisabled}
                  isLoading={isButtonLoading[index]}
                />
              </Tooltip>
            ) : (
              <Button
                key={index + " - " + isButtonLoading[index]}
                role={index === 2 ? "submit" : "button"}
                {...props}
                onClick={(e) => {
                  e.preventDefault();
                  onSubmitHandler(onClick, index);
                }}
                disabled={isDisabled}
                isLoading={isButtonLoading[index]}
              />
            );
          })}
        </div>
      </Footer>
    </Container>
  );
};

export default GoodsStockCheck;
